import {
  ICreditLineDetails,
  ITransaction,
  IWithdrawal,
  TransactionStatus,
  TransactionType,
  CreditLineDebitType,
} from "@/apps/credit-line/models/CreditLineDetails";

export const getLastWeekTransactions = (
  transactions: ITransaction[]
): ITransaction[] => {
  const now = new Date();
  const lastWeekDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 7
  ).setHours(0, 0, 0);
  return transactions.filter((t) => {
    const date = new Date(t?.date * 1000).getTime();
    return date >= lastWeekDate;
  });
};

export const getLastMonthTransactions = (
  transactions: ITransaction[]
): ITransaction[] => {
  const now = new Date();
  const lastMonthDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 30
  ).setHours(0, 0, 0);
  return transactions.filter((t) => {
    const tDate = new Date(t.date * 1000).getTime();
    return tDate >= lastMonthDate;
  });
};

export const getTransactionsInDateRange = (
  transactions: ITransaction[],
  range: Date[]
): ITransaction[] => {
  return transactions.filter((t) => {
    const tDate = new Date(t.date * 1000);
    const startDate = new Date(range[0].setHours(0, 0, 0)).getTime();
    const endDate = new Date(range[1].setHours(23, 59, 59)).getTime();
    return tDate.getTime() >= startDate && tDate.getTime() <= endDate;
  });
};

export const getTransactionsByCategory = (
  transactions: ITransaction[],
  category: string
): ITransaction[] => {
  switch (category) {
    case "card_transactions":
      return transactions.filter(
        (t) =>
          t.type === TransactionType.CREDITCARD ||
          t.type === TransactionType.PURCHASE_ADJUSTMENT
      );
    case "cash_withdrawals":
      return transactions.filter((t) => t.type === TransactionType.WITHDRAWAL);
    case "online_transfer":
      return transactions.filter(
        (t) => t.type === TransactionType.CASH_TRANSFER
      );
    case "phone_purchase":
      return transactions.filter(
        (t) => t.type === TransactionType.PHONE_PURCHASE
      );
    case "declined_transactions":
      return transactions.filter(
        (t) =>
          t.status === TransactionStatus.REJECTED ||
          t.status === TransactionStatus.EXPIRED
      );
    case "payments":
      return transactions.filter(
        (t) => !t.type || t.type === TransactionType.PAYMENT
      );
    case "refunds":
      return transactions.filter(
        (t) =>
          t.type === TransactionType.REFUND ||
          t.type === TransactionType.REVERSAL_PURCHASE
      );
    case "cash_back":
      return transactions.filter((t) => t.type === TransactionType.CASH_BACK);
    default:
      return [];
  }
};

export const populateTransactionsArray = (
  response: ICreditLineDetails
): ITransaction[] => {
  let transactions: ITransaction[] = [];
  transactions = response.payments
    ?.filter((x) => x?.paymentMethod !== "credit_adjustment")
    .map((payment) => ({
      ...payment,
      amount: `+${payment.paymentAmount}`,
      date: payment.paymentReceivedTime,
      type: "PAYMENT" as ITransaction["type"],
      status: TransactionStatus.PAID,
      method: payment.paymentMethod,
      category: "moneyIn",
    }));
  transactions.push(
    ...response.withdrawals.map((debit) => {
      return {
        ...debit,
        amount: `-${debit.amount}`,
        date: getTransactionDate(debit),
        type: getTransactionType(debit),
        status: getTransactionStatus(debit),
        method: debit.status === "REJECTED" ? debit.status : debit.debitSource,
        category: "moneyOut" as ITransaction["category"],
      };
    })
  );
  transactions.push(
    ...response.credits?.map((credit) => {
      return {
        ...credit,
        date: credit.transactionTime,
        method: credit.creditSource ?? credit.message,
        category: (credit.type === TransactionType.PURCHASE_ADJUSTMENT
          ? "moneyOut"
          : "moneyIn") as ITransaction["category"],
      };
    })
  );
  transactions.sort((x, y) => {
    return y.date - x.date;
  });
  return transactions;
};

export const getTransactionDate = (
  transaction: IWithdrawal
): ITransaction["date"] => {
  let transactionDate: ITransaction["date"];
  if (transaction.type === CreditLineDebitType.CREDITCARD) {
    transactionDate = transaction.requestedTime as ITransaction["date"];
    return transactionDate;
  }

  transactionDate = (transaction?.originalDisbursedTime ??
    transaction?.disbursedTime ??
    transaction?.expirationTime ??
    transaction?.requestedTime) as ITransaction["date"];
  return transactionDate;
};

export const getTransactionType = (
  transaction: IWithdrawal
): ITransaction["type"] => {
  if (
    (transaction.type as ITransaction["type"]) === TransactionType.PHONE_FINANCE
  ) {
    return TransactionType.PHONE_PURCHASE;
  } else {
    return (
      transaction.type ? transaction.type : "WITHDRAWAL"
    ) as ITransaction["type"];
  }
};

export const getTransactionStatus = (
  transaction: IWithdrawal
): ITransaction["status"] => {
  if (
    (transaction.type as ITransaction["type"]) === TransactionType.PHONE_FINANCE
  ) {
    return TransactionStatus.ACTIVE;
  } else {
    return transaction.status as ITransaction["status"];
  }
};

export const populateDaysArray = (arr: ITransaction[]): string[] => {
  let x = arr.map((y) => new Date(y.date * 1000).toLocaleDateString());
  x = x.reduce(
    (acc, val) => (acc.includes(val) ? acc : [...acc, val]),
    [] as string[]
  );
  return x;
};

export const getDebitById = (
  id: number | string | undefined,
  transactions: ITransaction[] | null | undefined
): ITransaction | undefined => {
  if (transactions) {
    const transaction =
      transactions.find((t) => t.debitId === id || t.paymentId === id) ?? null;
    return transaction ?? undefined;
  }
  return undefined;
};

export const getOriginalRefundDebitSourceById = (
  id: number | string | undefined,
  transactions: ITransaction[] | null | undefined
): string | undefined => {
  const debit = getDebitById(id, transactions);
  return debit?.method ?? debit?.debitSource ?? undefined;
};
